/**
 * API client wrapper — native fetch + RFC 7807 + Idempotency-Key + If-Match.
 *
 * Implementa decisiones G0 PWA-021:
 * - D5: Native fetch + custom wrapper (no axios/ky)
 * - D6: Idempotency-Key ULID generation per mutation intent
 *
 * Coordina con WORKER-023 (sprint-6 paralelo): los POST/PATCH llevan
 * Idempotency-Key ULID; PATCH lleva If-Match (ETag de la entidad current).
 */
import { ulid } from 'ulid';
import { env } from '../env.js';

// ─── Tipos ────────────────────────────────────────────────────────────────

export interface ApiProblem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  'trace-id'?: string;
  'error-code'?: string;
  'audit-event-id'?: string;
  'current-etag'?: string;
  'expected-tags'?: string[];
  [key: string]: unknown;
}

export class FwaApiError extends Error {
  public readonly problem: ApiProblem;
  public readonly status: number;
  public readonly etag: string | null;

  constructor(problem: ApiProblem, etag: string | null = null) {
    super(`${problem.status} ${problem.title}: ${problem.detail ?? ''}`);
    this.problem = problem;
    this.status = problem.status;
    this.etag = etag;
  }

  get errorCode(): string | undefined {
    return this.problem['error-code'];
  }

  get traceId(): string | undefined {
    return this.problem['trace-id'];
  }
}

export interface ApiRequestOptions {
  /** Custom headers (Authorization se inyecta automáticamente cuando esté disponible) */
  headers?: Record<string, string>;
  /** Idempotency-Key generado per intent (POST/PATCH). Si no se pasa, helper genera ULID nuevo. */
  idempotencyKey?: string;
  /** ETag value para If-Match header (PATCH endpoints). Sin quotes — wrapper agrega quoting. */
  ifMatch?: string;
  /** AbortSignal para cancelar fetch */
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  etag: string | null;
  /** Header Location (POST 201) parseado */
  location: string | null;
  /** `original` (1ra request) / `replayed` (replay via Idempotency-Key) */
  idempotencyStatus: 'original' | 'replayed' | null;
  status: number;
}

// ─── Authorization injector (defer to PWA-022 auth flow) ──────────────────

/**
 * V1 placeholder. PWA-022 implementará bearer extraction desde CF Access cookie
 * o JWT en memory state. Por ahora retorna null → no Authorization header.
 */
function getAuthToken(): string | null {
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Genera Idempotency-Key ULID (per D6 — per mutation intent).
 *
 * En componentes con `useMutation`, cada call a `mutate()` genera un nuevo key
 * (via TanStack Query mutationKey + closure). Reintentos de la misma mutation
 * vía `retry` reusan el key vía closure.
 */
export function generateIdempotencyKey(): string {
  return ulid();
}

function buildHeaders(opts: ApiRequestOptions = {}, isWrite = false): Headers {
  const h = new Headers({
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-SISREP-Distrito': env.DISTRITO_ID,
  });
  const token = getAuthToken();
  if (token) h.set('Authorization', `Bearer ${token}`);
  if (isWrite) {
    h.set('Idempotency-Key', opts.idempotencyKey ?? generateIdempotencyKey());
  }
  if (opts.ifMatch) {
    h.set('If-Match', `"${opts.ifMatch}"`);
  }
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) h.set(k, v);
  }
  return h;
}

/**
 * 401 handler — dispatch logout via global escape hatch (D6 PWA-022).
 * Skip para `/v1/auth/verify` (initial verify legitimately retorna 401 si no auth).
 */
function trigger401Logout(path: string): void {
  if (path.includes('/v1/auth/verify')) return;
  if (typeof window !== 'undefined' && window.__sisrep_auth) {
    window.__sisrep_auth.logout();
  }
}

async function parseResponse<T>(res: Response, path: string): Promise<ApiResponse<T>> {
  const etagHeader = res.headers.get('ETag');
  const etag = etagHeader ? etagHeader.replace(/^W\//, '').replace(/^"|"$/g, '') : null;
  const idempotencyStatusHeader = res.headers.get('Idempotency-Status');
  const idempotencyStatus =
    idempotencyStatusHeader === 'original' || idempotencyStatusHeader === 'replayed'
      ? idempotencyStatusHeader
      : null;

  if (!res.ok) {
    if (res.status === 401) {
      trigger401Logout(path);
    }
    const contentType = res.headers.get('Content-Type') ?? '';
    if (
      contentType.includes('application/problem+json') ||
      contentType.includes('application/json')
    ) {
      const problem = (await res.json()) as ApiProblem;
      throw new FwaApiError(problem, etag);
    }
    const text = await res.text();
    throw new FwaApiError(
      {
        type: 'about:blank',
        title: 'Unexpected error',
        status: res.status,
        detail: text.slice(0, 200),
      },
      etag,
    );
  }

  const data = res.status === 204 ? (null as T) : ((await res.json()) as T);
  return {
    data,
    etag,
    location: res.headers.get('Location'),
    idempotencyStatus,
    status: res.status,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────

// V1.0.1 patch: credentials: 'include' es CRÍTICO para CF Access cross-domain.
// PWA en sisrep.trefoilhub.com hace fetch al Worker en api.trefoilhub.com.
// Mismo eTLD+1 trefoilhub.com + misma CF Access app → cookies cross-subdomain OK.
// Sin credentials: 'include', el browser NO envía cookies → CF Access intercepta
// cada request porque no detecta auth previa → 302 redirect → CORS fail.

export async function apiGet<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${env.WORKER_URL}${path}`, {
    method: 'GET',
    headers: buildHeaders(opts, false),
    credentials: 'include',
    signal: opts.signal ?? null,
  });
  return parseResponse<T>(res, path);
}

export async function apiPost<T, B = unknown>(
  path: string,
  body: B,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${env.WORKER_URL}${path}`, {
    method: 'POST',
    headers: buildHeaders(opts, true),
    body: JSON.stringify(body),
    credentials: 'include',
    signal: opts.signal ?? null,
  });
  return parseResponse<T>(res, path);
}

export async function apiPatch<T, B = unknown>(
  path: string,
  body: B,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${env.WORKER_URL}${path}`, {
    method: 'PATCH',
    headers: buildHeaders(opts, true),
    body: JSON.stringify(body),
    credentials: 'include',
    signal: opts.signal ?? null,
  });
  return parseResponse<T>(res, path);
}

export async function apiDelete<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${env.WORKER_URL}${path}`, {
    method: 'DELETE',
    headers: buildHeaders(opts, true),
    credentials: 'include',
    signal: opts.signal ?? null,
  });
  return parseResponse<T>(res, path);
}
